import { calculateProposalBudget, budgetFxRate, normalizeBudgetRates, BUDGET_FX_UNAVAILABLE } from '@/lib/proposalBudget';
import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import {
  Sun,
  Plane,
  Clock,
  Check,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Star,
  Building2,
  Utensils,
  Sparkles,
  Receipt,
  CircleDollarSign,
  Play,
  X,
  Pause,
  Volume2,
  VolumeX,
  Image as ImageIcon,
  Moon,
  CloudRain,
  ArrowRight,
  Plus,
  Users,
  Car,
  Calendar as CalendarIcon
} from 'lucide-react';
import { cn, convertTemperature, getTemperatureUnitSymbol } from '@/lib/utils';
import { StarRating } from '@/components/ui/StarRating';
import { proposalTokens } from './tokens';
import { HotelDetailsModal } from './HotelDetailsModal';
import { ActivityDetailsModal } from './ActivityDetailsModal';
import { EventDetailsModal } from './EventDetailsModal';
import ClimateReportModal from './ClimateReportModal';
import { proposalService } from '@/services/ProposalService';
import { formatCurrency as formatCurrencyAmount } from '@/lib/currencyFormat';
import { parseDateOnly } from '@/lib/dateFormatters';
import type { VenueRoomBreakdown, VenueActivitiesBreakdown, VenueMealsBreakdown, VenueMeetingsBreakdown, VenueOtherCosts, BudgetSettings } from '@/services/BudgetService';

// Types
interface DestinationMedia {
  type: 'image' | 'video';
  url: string;
  caption?: string;
  thumbnail?: string;
}

interface Hotel {
  id: string;
  name: string;
  stars: number;
  image: string;
  images?: string[];
  description?: string;
  roomCount?: number;
  meetingSpaces?: string[];
  priceRange?: string;
  highlights?: string[];
  recommended?: boolean;
  buyout?: boolean;
  allInclusive?: boolean;
  city?: string;
  availableDates?: { start: string; end: string };
  /** Client-facing hotel note (internal notes are never returned publicly). */
  clientNote?: string;
}

interface Activity {
  id: string;
  title: string;
  category: string;
  image: string;
  description?: string;
  duration?: string;
  intensity?: 'Active' | 'Moderate' | 'Relaxed';
}

interface DestinationEvent {
  id: string;
  title: string;
  tag: string;
  image: string;
  description?: string;
  participants?: string;
  pricePerPerson?: number;
}

interface PricingBreakdown {
  accommodation: number;
  foodAndBev: number;
  activities: number;
  transport: number;
}

interface Pricing {
  totalEstimated: number;
  perPerson: number;
  currency: string;
  breakdown: PricingBreakdown;
}

interface DestinationData {
  id: string;
  city: string;
  country: string;
  heroImage?: string;
  tagline?: string;
  description?: string;
  pros?: string[];
  hotels?: Hotel[];
  activities?: Activity[];
  events?: DestinationEvent[];
  pricing?: Pricing;
  media?: {
    layout?: 'auto' | 'collage' | 'carousel' | 'video-hero' | 'hero';
    videoMode?: 'modal' | 'background' | 'inline';
    items?: DestinationMedia[];
  };
}

interface DestinationContent {
  flights_transportation?: number | null;
  food_beverage?: number | null;
  activities_meetings?: number | null;
  miscellaneous?: number | null;
  // New granular pricing fields
  ground_transport?: number | null;
  meals_cost?: number | null;
  meetings_cost?: number | null;
  activities_cost?: number | null;
  input_currency?: string | null;
  day_avg_temperature?: number | null;
  night_avg_temperature?: number | null;
  rainfall?: number | null;
  rainy_days?: number | null;
  humidity?: number | null;
  weather_description?: string | null;
  travel_tip_weather?: string | null;
  travel_tip_packing?: string | null;
  travel_tip_local_events?: string | null;
  annual_climate_data?: Array<{
    month: string;
    avg_high: number;
    avg_low: number;
    rainfall: number;
  }> | null;
}

interface DestinationProps {
  destination: DestinationData;
  groupSize?: number;
  venueData?: Array<{
    venue: any;
    shortlistItem: any;
  }>;
  destinationContent?: DestinationContent;
  shareLinkToken?: string;
  sessionId?: string;
  eventId?: string; // Event ID for fetching budget data
  /** Team switch in Budget Manager. When false no budget figures are fetched or shown. */
  showBudget?: boolean;
  onSelectPackage?: (destId: string, hotelId?: string) => void;
  onAddToFavorites?: (destinationId: string, hotelId: string) => void;
  onViewHotelDetails?: (hotelId: string) => void;
  shortlistedHotelIds?: Set<string>;
  eventDateType?: 'date_range' | 'month' | null;
  eventMonth?: string | null;
  eventStartDate?: string | null; // ISO date string
  eventEndDate?: string | null; // ISO date string
  eventDateDisplayOverride?: string | null;
  temperatureUnit?: 'celsius' | 'fahrenheit' | null; // Temperature unit preference from event settings
  distanceUnit?: 'kilometers' | 'miles' | null; // Distance unit preference from event settings
  onReturnToProposal?: () => void; // Navigate back to the proposal overview
}

// ========================
// UTILITY FUNCTIONS
// ========================

/**
 * Extracts the first sentence from a description text.
 * A sentence is defined as text ending with '.', '!', or '?' followed by a space or end of string.
 * If no sentence-ending punctuation is found, it returns the first 100 characters.
 */
function getFirstSentence(text: string): { firstSentence: string; hasMore: boolean } {
  if (!text || text.trim().length === 0) {
    return { firstSentence: '', hasMore: false };
  }

  const trimmed = text.trim();

  // Try to match first sentence: text ending with . ! or ? followed by space or end of string
  // Updated regex to handle cases where punctuation might be followed immediately by text
  const sentenceMatch = trimmed.match(/^[^.!?]*[.!?](?:\s|$)/);

  if (sentenceMatch) {
    const firstSentence = sentenceMatch[0].trim();
    const remaining = trimmed.slice(firstSentence.length).trim();
    // Consider it has more if there's remaining text after trimming
    return { firstSentence, hasMore: remaining.length > 0 };
  }

  // If no sentence-ending punctuation found, check if text is longer than 100 chars
  // If so, show first 100 chars with ellipsis
  if (trimmed.length > 100) {
    return { firstSentence: trimmed.substring(0, 100).trim() + '...', hasMore: true };
  }

  // Short text with no sentence punctuation - show all
  return { firstSentence: trimmed, hasMore: false };
}

// ========================
// ACTIVITY DESCRIPTION COMPONENT
// Used for events (activities use ActivityDetailsModal instead)
// ========================

interface ActivityDescriptionProps {
  description: string;
  tealColor: string;
}

const ActivityDescription: React.FC<ActivityDescriptionProps> = ({ description, tealColor }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  const [popoverPosition, setPopoverPosition] = useState({ top: 0, left: 0 });
  const popoverRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const { firstSentence, hasMore } = getFirstSentence(description);
  // Show tooltip if there's more content OR if the description is significantly longer than what we're showing
  const shouldShowTooltip = hasMore || description.trim().length > firstSentence.length + 10;

  // Detect touch device
  useEffect(() => {
    setIsTouchDevice('ontouchstart' in window || navigator.maxTouchPoints > 0);
  }, []);

  // Update popover position when open
  useEffect(() => {
    if (!isOpen || !buttonRef.current) return;

    const updatePosition = () => {
      if (!buttonRef.current) return;
      const rect = buttonRef.current.getBoundingClientRect();
      const popoverWidth = 280; // Approximate width

      // Calculate left position, ensuring it doesn't go off screen
      let left = rect.left + rect.width / 2 - popoverWidth / 2;
      const padding = 16;

      // Clamp to viewport
      if (left < padding) left = padding;
      if (left + popoverWidth > window.innerWidth - padding) {
        left = window.innerWidth - popoverWidth - padding;
      }

      setPopoverPosition({
        top: rect.top - 8, // Position above the button with gap
        left: left,
      });
    };

    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen]);

  // Close popover when clicking outside (for mobile)
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isOpen]);

  const handleButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    // Toggle on click for mobile, or just keep open for desktop
    if (isTouchDevice) {
      setIsOpen(!isOpen);
    }
  };

  const handleMouseEnter = () => {
    if (!isTouchDevice) {
      setIsOpen(true);
    }
  };

  const handleMouseLeave = () => {
    if (!isTouchDevice) {
      setIsOpen(false);
    }
  };

  // Render popover in portal to avoid overflow clipping
  const renderPopover = () => {
    if (!isOpen) return null;

    return createPortal(
      <div
        ref={popoverRef}
        className="fixed z-[9999] animate-in fade-in-0 duration-150"
        style={{
          top: popoverPosition.top,
          left: popoverPosition.left,
          transform: 'translateY(-100%)',
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div
          className="bg-white rounded-lg shadow-xl border border-gray-200 p-3"
          style={{ maxWidth: '280px', minWidth: '200px' }}
        >
          <p className="text-sm whitespace-pre-wrap text-gray-700 leading-relaxed">
            {description}
          </p>
        </div>
        {/* Arrow */}
        <div
          className="absolute left-1/2 -translate-x-1/2 -bottom-1.5 w-3 h-3 bg-white border-r border-b border-gray-200 rotate-45"
        />
      </div>,
      document.body
    );
  };

  return (
    <div className="mb-4 flex-1">
      <div className="flex items-start gap-2">
        <p className="text-sm flex-1" style={{ color: `${tealColor}99` }}>
          {firstSentence}
        </p>
        {shouldShowTooltip && (
          <div className="relative">
            <button
              ref={buttonRef}
              type="button"
              className="shrink-0 w-5 h-5 rounded-full flex items-center justify-center transition-all hover:scale-110 cursor-pointer border"
              style={{
                color: tealColor,
                borderColor: isOpen ? tealColor : `${tealColor}40`,
                backgroundColor: isOpen ? `${tealColor}15` : 'transparent'
              }}
              onMouseEnter={(e) => {
                handleMouseEnter();
                if (!isOpen) {
                  e.currentTarget.style.backgroundColor = `${tealColor}15`;
                  e.currentTarget.style.borderColor = tealColor;
                }
              }}
              onMouseLeave={(e) => {
                handleMouseLeave();
                if (!isOpen) {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.borderColor = `${tealColor}40`;
                }
              }}
              onClick={handleButtonClick}
              aria-label="Show full description"
              aria-expanded={isOpen}
            >
              <Plus className="w-3.5 h-3.5" strokeWidth={2.5} />
            </button>

            {/* Render popover in portal */}
            {renderPopover()}
          </div>
        )}
      </div>
    </div>
  );
};

// ========================
// MEDIA GALLERY COMPONENTS
// ========================

// Collage Layout - 3 Images
const CollageLayout: React.FC<{ items: DestinationMedia[], fallbackHero: string }> = ({ items, fallbackHero }) => {
  const images = items.length >= 3
    ? items.slice(0, 3)
    : [
        { type: 'image' as const, url: fallbackHero },
        { type: 'image' as const, url: `https://picsum.photos/seed/col1/800/800` },
        { type: 'image' as const, url: `https://picsum.photos/seed/col2/800/800` },
      ];

  return (
    <div className="flex flex-col gap-4 w-full rounded-3xl overflow-hidden">
      {/* Main Hero - Top Horizontal (2:1 aspect ratio) */}
      <div className="relative overflow-hidden group w-full" style={{ aspectRatio: '2 / 1' }}>
        <img src={images[0].url} className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110" alt={images[0].caption || 'Main'} loading="lazy" />
      </div>
      {/* Bottom Row - Two 1:1 images */}
      <div className="grid grid-cols-2 gap-4">
        <div className="relative overflow-hidden group" style={{ aspectRatio: '1 / 1' }}>
          <img src={images[1].url} className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110" alt={images[1].caption || 'Detail 1'} loading="lazy" />
        </div>
        <div className="relative overflow-hidden group" style={{ aspectRatio: '1 / 1' }}>
          <img src={images[2].url} className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110" alt={images[2].caption || 'Detail 2'} loading="lazy" />
        </div>
      </div>
    </div>
  );
};

// Carousel Layout - Multiple Images with Navigation
const CarouselLayout: React.FC<{ items: DestinationMedia[] }> = ({ items }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const imageItems = items.filter(item => item.type === 'image');

  useEffect(() => {
    if (!isAutoPlaying || imageItems.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % imageItems.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [isAutoPlaying, imageItems.length]);

  const goTo = (index: number) => {
    setCurrentIndex(index);
    setIsAutoPlaying(false);
  };

  const goNext = () => goTo((currentIndex + 1) % imageItems.length);
  const goPrev = () => goTo((currentIndex - 1 + imageItems.length) % imageItems.length);

  return (
    <div className="relative h-full w-full rounded-3xl overflow-hidden group">
      {/* Main Image */}
      <div className="relative h-full w-full">
        {imageItems.map((item, index) => (
          <div
            key={index}
            className={cn(
              "absolute inset-0 transition-all duration-700 ease-out",
              index === currentIndex
                ? 'opacity-100 scale-100'
                : 'opacity-0 scale-105'
            )}
          >
            <img
              src={item.url}
              className="w-full h-full object-cover"
              alt={item.caption || `Slide ${index + 1}`}
              loading="lazy"
            />
          </div>
        ))}
      </div>

      {/* Navigation Arrows */}
      {imageItems.length > 1 && (
        <>
          <button
            onClick={goPrev}
            className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-md bg-white/20 backdrop-blur-md text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all hover:bg-white/40 hover:scale-110"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button
            onClick={goNext}
            className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-md bg-white/20 backdrop-blur-md text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all hover:bg-white/40 hover:scale-110"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        </>
      )}

      {/* Dot Indicators */}
      {imageItems.length > 1 && (
        <div className="absolute bottom-6 right-6 md:left-1/2 md:-translate-x-1/2 md:right-auto flex items-center gap-2">
          {imageItems.map((_, index) => (
            <button
              key={index}
              onClick={() => goTo(index)}
              className={cn(
                "h-2 rounded-full transition-all duration-300",
                index === currentIndex
                  ? 'w-8 bg-white'
                  : 'w-2 bg-white/50 hover:bg-white/80'
              )}
            />
          ))}
        </div>
      )}

      {/* Image Counter */}
      <div className="absolute top-4 right-4 bg-black/50 backdrop-blur-sm px-3 py-1.5 rounded-full flex items-center gap-2 text-white/80 text-sm">
        <ImageIcon className="w-4 h-4" />
        <span>{currentIndex + 1} / {imageItems.length}</span>
      </div>
    </div>
  );
};

// Video Hero Layout - Full video with play button
const VideoHeroLayout: React.FC<{
  items: DestinationMedia[],
  videoMode: 'modal' | 'background' | 'inline',
  destinationName: string
}> = ({ items, videoMode, destinationName }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPlaying, setIsPlaying] = useState(videoMode === 'background');
  const [isMuted, setIsMuted] = useState(videoMode === 'background');
  const videoRef = useRef<HTMLVideoElement>(null);
  const modalVideoRef = useRef<HTMLVideoElement>(null);

  const videoItem = items.find(item => item.type === 'video');
  const thumbnail = videoItem?.thumbnail || items.find(item => item.type === 'image')?.url || '';

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const openModal = () => {
    setIsModalOpen(true);
    setTimeout(() => {
      modalVideoRef.current?.play();
    }, 100);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    modalVideoRef.current?.pause();
  };

  // Modal video mode - thumbnail with play button
  if (videoMode !== 'background') {
    return (
      <>
        <div className="relative h-full w-full rounded-3xl overflow-hidden group cursor-pointer" onClick={openModal}>
          <img
            src={thumbnail}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            alt={destinationName}
            loading="lazy"
          />
          <div className="absolute inset-0 bg-black/30 group-hover:bg-black/40 transition-colors"></div>

          {/* Play Button */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-24 h-24 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center group-hover:scale-110 transition-transform border-2 border-white/50">
              <div className="w-20 h-20 rounded-full bg-white flex items-center justify-center">
                <Play className="w-10 h-10 ml-1" style={{ color: proposalTokens.colors.teal, fill: proposalTokens.colors.teal }} />
              </div>
            </div>
          </div>

          {/* Video indicator */}
          <div className="absolute bottom-4 left-4 bg-black/60 backdrop-blur-sm px-4 py-2 rounded-lg flex items-center gap-2 text-white">
            <Play className="w-4 h-4" />
            <span className="text-sm font-medium">Watch Video</span>
          </div>
        </div>

        {/* Video Modal */}
        {isModalOpen && (
          <div
            className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4 md:p-8 animate-fade-in"
            onClick={closeModal}
          >
            <div
              className="relative w-full max-w-5xl aspect-video rounded-2xl overflow-hidden"
              onClick={e => e.stopPropagation()}
            >
              <video
                ref={modalVideoRef}
                src={videoItem?.url}
                poster={thumbnail}
                controls
                autoPlay
                className="w-full h-full object-cover"
              />
            </div>
            <button
              onClick={closeModal}
              className="absolute top-6 right-6 w-12 h-12 rounded-md bg-white/10 text-white flex items-center justify-center hover:bg-white/20 transition-all"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        )}
      </>
    );
  }

  // Background video mode - auto-play muted, no overlay/play button
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);

  return (
    <div className="relative h-full w-full rounded-3xl overflow-hidden group bg-gray-900">
      {/* Loading State / Poster */}
      <div
        className={cn(
          "absolute inset-0 z-10 transition-opacity duration-700",
          isVideoLoaded ? 'opacity-0 pointer-events-none' : 'opacity-100'
        )}
      >
        <img
          src={thumbnail}
          className="w-full h-full object-cover"
          alt={destinationName}
          loading="lazy"
        />
        <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
          <div className="w-12 h-12 border-4 border-white/20 border-t-white rounded-full animate-spin"></div>
        </div>
      </div>

      <video
        ref={videoRef}
        src={videoItem?.url}
        poster={thumbnail}
        loop
        muted={isMuted}
        autoPlay
        playsInline
        preload="auto"
        onCanPlay={() => setIsVideoLoaded(true)}
        onLoadedData={() => setIsVideoLoaded(true)}
        className="w-full h-full object-cover"
      />

      {/* Dark Overlay - fades out on hover */}
      <div className="absolute inset-0 bg-black/40 transition-opacity duration-500 group-hover:opacity-0 pointer-events-none"></div>

      {/* Overlay gradient - fades out on hover */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/30 transition-opacity duration-500 group-hover:opacity-0 pointer-events-none"></div>

      {/* Controls - Hidden by default, visible on hover */}
      <div className="absolute bottom-4 right-4 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-20">
        <button
          onClick={togglePlay}
          className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-md text-white flex items-center justify-center hover:bg-white/40 transition-all"
        >
          {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
        </button>
        <button
          onClick={() => setIsMuted(!isMuted)}
          className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-md text-white flex items-center justify-center hover:bg-white/40 transition-all"
        >
          {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
        </button>
      </div>
    </div>
  );
};

// Single Hero Image Layout
const HeroLayout: React.FC<{ items: DestinationMedia[], fallbackHero: string }> = ({ items, fallbackHero }) => {
  const heroImage = items[0]?.url || fallbackHero;
  const caption = items[0]?.caption;

  return (
    <div className="relative h-full w-full rounded-3xl overflow-hidden group">
      <img
        src={heroImage}
        className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105"
        alt="Destination"
        loading="lazy"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>
    </div>
  );
};

// Empty Media State - When no images or video are uploaded
const EmptyMediaLayout: React.FC<{ destinationName: string }> = ({ destinationName }) => {
  return (
    <div className="relative h-full w-full rounded-3xl overflow-hidden flex items-center justify-center" style={{ backgroundColor: proposalTokens.colors.cream }}>
      <div className="text-center p-8">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ backgroundColor: `${proposalTokens.colors.teal}15` }}>
          <ImageIcon className="w-8 h-8" style={{ color: `${proposalTokens.colors.teal}40` }} />
        </div>
        <p className="text-sm font-medium" style={{ color: `${proposalTokens.colors.teal}60` }}>{destinationName}</p>
      </div>
      {/* Subtle gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/5 via-transparent to-transparent pointer-events-none"></div>
    </div>
  );
};

// Main Media Gallery Component
const MediaGallery: React.FC<{ destination: DestinationData }> = ({ destination }) => {
  const media = destination.media;
  const fallbackHero = destination.heroImage || 'https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=1200&q=80';

  // If no media at all, show empty state
  if (!media || !media.items || media.items.length === 0) {
    return <EmptyMediaLayout destinationName={destination.city} />;
  }

  const { layout = 'auto', videoMode = 'modal', items = [] } = media;
  const hasVideo = items.some(item => item.type === 'video');
  const imageCount = items.filter(item => item.type === 'image').length;

  // If no images and no video, show empty state
  if (!hasVideo && imageCount === 0) {
    return <EmptyMediaLayout destinationName={destination.city} />;
  }

  let resolvedLayout = layout;
  if (layout === 'auto') {
    if (hasVideo) {
      resolvedLayout = 'video-hero';
    } else if (imageCount > 3) {
      resolvedLayout = 'carousel';
    } else if (imageCount === 3) {
      resolvedLayout = 'collage';
    } else if (imageCount > 0) {
      resolvedLayout = 'hero';
    } else {
      // No images, no video - should not reach here but handle gracefully
      return <EmptyMediaLayout destinationName={destination.city} />;
    }
  }

  switch (resolvedLayout) {
    case 'video-hero':
      return <VideoHeroLayout items={items} videoMode={videoMode} destinationName={destination.city} />;
    case 'carousel':
      return <CarouselLayout items={items} />;
    case 'collage':
      return <CollageLayout items={items} fallbackHero={fallbackHero} />;
    case 'hero':
    default:
      return <HeroLayout items={items} fallbackHero={fallbackHero} />;
  }
};

// ========================
// PROS SECTION COMPONENT
// ========================

const ProsSection: React.FC<{ pros: string[], destinationName: string }> = ({ pros, destinationName }) => {
  // Ensure pros is always an array
  const prosArray = Array.isArray(pros) ? pros : [];
  if (prosArray.length === 0) return null;

  return (
    <div className="mt-8">
      <h4 className="text-sm font-bold uppercase tracking-wider mb-4" style={{ color: `${proposalTokens.colors.teal}66` }}>
        Why {destinationName}
      </h4>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {prosArray.map((pro, index) => (
          <div
            key={index}
            className="flex items-start gap-3 p-3 bg-white rounded-xl border border-black/5 shadow-sm hover:shadow-md transition-shadow group"
          >
            <div className="w-6 h-6 rounded-full bg-emerald-500/10 flex items-center justify-center shrink-0 group-hover:bg-emerald-500/20 transition-colors">
              <Check className="w-4 h-4 text-emerald-600" />
            </div>
            <span className="text-sm leading-relaxed" style={{ color: `${proposalTokens.colors.teal}cc` }}>{pro}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ========================
// MAIN DESTINATION COMPONENT
// ========================

export function Destination({ destination, groupSize = 50, venueData = [], destinationContent, shareLinkToken, sessionId, eventId, onSelectPackage, onAddToFavorites, onViewHotelDetails, shortlistedHotelIds, eventDateType, eventMonth, eventStartDate, eventEndDate, eventDateDisplayOverride, temperatureUnit = 'celsius', distanceUnit = 'kilometers', onReturnToProposal, showBudget = true }: DestinationProps) {
  const [isClimateModalOpen, setIsClimateModalOpen] = useState(false);

  // Calculate travel month from event dates or event month
  const getTravelMonth = (): string => {
    // If we have explicit start/end dates, derive month(s) from them
    if (eventStartDate) {
      const startDate = new Date(eventStartDate);
      const endDate = eventEndDate ? new Date(eventEndDate) : startDate;

      const startMonth = startDate.toLocaleString('en-US', { month: 'long' });
      const endMonth = endDate.toLocaleString('en-US', { month: 'long' });

      if (startMonth === endMonth) {
        return startMonth;
      }

      // If spans two months, return "May-June" format
      return `${startMonth}-${endMonth}`;
    }

    // Fall back to eventMonth (stored as "YYYY-MM" format, e.g. "2026-04")
    if (eventMonth) {
      const parsed = new Date(`${eventMonth}-01T00:00:00`);
      if (!isNaN(parsed.getTime())) {
        return parsed.toLocaleString('en-US', { month: 'long' });
      }
      // If it's already a month name (e.g. "October"), return as-is
      return eventMonth;
    }

    return 'May'; // Last resort fallback
  };

  const travelMonth = getTravelMonth();

  // Extract weather data from destinationContent and convert temperatures based on preference
  const weatherData = destinationContent ? {
    day_avg_temperature: convertTemperature(destinationContent.day_avg_temperature ?? null, temperatureUnit),
    night_avg_temperature: convertTemperature(destinationContent.night_avg_temperature ?? null, temperatureUnit),
    rainfall: destinationContent.rainfall ?? null,
    rainy_days: destinationContent.rainy_days ?? null,
    humidity: destinationContent.humidity ?? null,
    weather_description: destinationContent.weather_description ?? null,
    travel_tip_weather: destinationContent.travel_tip_weather ?? null,
    travel_tip_packing: destinationContent.travel_tip_packing ?? null,
    travel_tip_local_events: destinationContent.travel_tip_local_events ?? null,
    annual_climate_data: destinationContent.annual_climate_data ? destinationContent.annual_climate_data.map(item => ({
      month: item.month,
      avg_high: convertTemperature(item.avg_high, temperatureUnit) ?? item.avg_high,
      avg_low: convertTemperature(item.avg_low, temperatureUnit) ?? item.avg_low,
      rainfall: item.rainfall,
    })) : null,
  } : null;

  // Get temperature unit symbol for display
  const tempUnitSymbol = getTemperatureUnitSymbol(temperatureUnit);
  const [selectedHotel, setSelectedHotel] = useState<Hotel | null>(null);
  const [selectedVenueData, setSelectedVenueData] = useState<{ venue: any; shortlistItem: any } | null>(null);
  const [selectedHotelForPricing, setSelectedHotelForPricing] = useState<string | null>(null);
  const [selectedActivity, setSelectedActivity] = useState<Activity | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<DestinationEvent | null>(null);
  const experienceScrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const eventScrollRef = useRef<HTMLDivElement>(null);
  const [canScrollEventsLeft, setCanScrollEventsLeft] = useState(false);
  const [canScrollEventsRight, setCanScrollEventsRight] = useState(false);

  // Budget data state
  const [budgetBreakdown, setBudgetBreakdown] = useState<{
    roomBreakdown: VenueRoomBreakdown | null;
    activitiesBreakdown: VenueActivitiesBreakdown | null;
    mealsBreakdown: VenueMealsBreakdown | null;
    meetingsBreakdown: VenueMeetingsBreakdown | null;
    otherCosts: VenueOtherCosts | null;
  } | null>(null);
  const [fullBudget, setFullBudget] = useState<any>(null);
  const [exchangeRates, setExchangeRates] = useState<any>(null);
  const [budgetLoading, setBudgetLoading] = useState(false);

  // Expandable sections state
  const [expandedSections, setExpandedSections] = useState<{
    meals: boolean;
    meetings: boolean;
    misc: boolean;
  }>({
    meals: false,
    meetings: false,
    misc: false,
  });

  const toggleSection = (section: 'meals' | 'meetings' | 'misc') => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  // Placeholder data for missing fields
  const description = destination.description || `Discover ${destination.city}, ${destination.country} - a perfect destination for your team retreat.`;
  const pros = destination.pros || [];
  const hotels = destination.hotels || [];
  const activities = destination.activities || [];
  const events = destination.events || [];

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  // Check scroll position to show/hide arrows
  const updateScrollArrows = useCallback(() => {
    const container = experienceScrollRef.current;
    if (!container || activities.length === 0) {
      setCanScrollLeft(false);
      setCanScrollRight(false);
      return;
    }

    const scrollLeft = container.scrollLeft;
    const scrollWidth = container.scrollWidth;
    const clientWidth = container.clientWidth;

    // Show left arrow if we've scrolled away from the start
    setCanScrollLeft(scrollLeft > 10);
    // Show right arrow if there's more content to the right
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
  }, [activities.length]);

  // Check if scrolling is needed and update arrows on scroll
  useEffect(() => {
    const container = experienceScrollRef.current;
    if (!container) return;

    // Initial check
    updateScrollArrows();

    // Listen for scroll events
    container.addEventListener('scroll', updateScrollArrows);
    window.addEventListener('resize', updateScrollArrows);

    return () => {
      container.removeEventListener('scroll', updateScrollArrows);
      window.removeEventListener('resize', updateScrollArrows);
    };
  }, [updateScrollArrows]);

  const scrollExperiences = (direction: 'left' | 'right') => {
    if (experienceScrollRef.current) {
      const scrollAmount = direction === 'left' ? -400 : 400;
      experienceScrollRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  // Check scroll position for events to show/hide arrows
  const updateEventsScrollArrows = useCallback(() => {
    const container = eventScrollRef.current;
    if (!container || events.length === 0) {
      setCanScrollEventsLeft(false);
      setCanScrollEventsRight(false);
      return;
    }

    const scrollLeft = container.scrollLeft;
    const scrollWidth = container.scrollWidth;
    const clientWidth = container.clientWidth;

    setCanScrollEventsLeft(scrollLeft > 10);
    setCanScrollEventsRight(scrollLeft < scrollWidth - clientWidth - 10);
  }, [events.length]);

  // Check if scrolling is needed and update arrows on scroll for events
  useEffect(() => {
    const container = eventScrollRef.current;
    if (!container) return;

    updateEventsScrollArrows();

    container.addEventListener('scroll', updateEventsScrollArrows);
    window.addEventListener('resize', updateEventsScrollArrows);

    return () => {
      container.removeEventListener('scroll', updateEventsScrollArrows);
      window.removeEventListener('resize', updateEventsScrollArrows);
    };
  }, [updateEventsScrollArrows]);

  const scrollEvents = (direction: 'left' | 'right') => {
    if (eventScrollRef.current) {
      const scrollAmount = direction === 'left' ? -400 : 400;
      eventScrollRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };
  // Set default hotel for pricing when hotels are available
  useEffect(() => {
    if (hotels.length > 0) {
      // If no hotel is selected, or selected hotel doesn't exist, use first hotel
      if (!selectedHotelForPricing) {
        setSelectedHotelForPricing(hotels[0].id);
      } else {
        const hotelExists = hotels.some(h => h.id === selectedHotelForPricing);
        if (!hotelExists) {
          setSelectedHotelForPricing(hotels[0].id);
        }
      }
    }
  }, [hotels.length, hotels, selectedHotelForPricing]);

  // Fetch exchange rates on mount
  useEffect(() => {
    if (shareLinkToken) {
      proposalService.getExchangeRates()
        .then(data => {
          // API returns { rates: { base: 'USD', rates: {...}, timestamp: ... } }
          // Keep the full ExchangeRates structure to match Trip Breakdown format
          const exchangeRatesData = data.rates || data;
          setExchangeRates(exchangeRatesData);
        })
        .catch(err => console.error('Failed to fetch exchange rates:', err));
    }
  }, [shareLinkToken]);

  // Fetch full budget when eventId is available
  useEffect(() => {
    if (showBudget && eventId && shareLinkToken && sessionId) {
      setBudgetLoading(true);
      proposalService.getFullBudget(shareLinkToken, eventId, sessionId)
        .then(data => {
          setFullBudget(data.budget);
          setBudgetLoading(false);
        })
        .catch(err => {
          console.error('Failed to fetch full budget:', err);
          setBudgetLoading(false);
        });
    }
  }, [showBudget, eventId, shareLinkToken, sessionId]);

  // Fetch venue budget breakdown when hotel is selected
  useEffect(() => {
    if (showBudget && selectedHotelForPricing && venueData.length > 0 && shareLinkToken && sessionId) {
      const venueInfo = venueData.find(v => v.venue.id === selectedHotelForPricing);
      if (venueInfo?.shortlistItem?.id) {
        setBudgetLoading(true);
        proposalService.getVenueBudgetBreakdown(shareLinkToken, venueInfo.shortlistItem.id, sessionId)
          .then(data => {
            setBudgetBreakdown(data);
            setBudgetLoading(false);
          })
          .catch(err => {
            console.error('Failed to fetch venue budget breakdown:', err);
            setBudgetLoading(false);
          });
      }
    }
  }, [showBudget, selectedHotelForPricing, venueData, shareLinkToken, sessionId]);

  const convertCurrency = useCallback((amount: number, fromCurrency: string, toCurrency: string) =>
    amount * budgetFxRate(fromCurrency, toCurrency, normalizeBudgetRates(exchangeRates)), [exchangeRates]);

  // Get event currency (default to USD) — source of truth is event.base_currency
  const eventCurrency = fullBudget?.base_currency || 'USD';
  const venueCurrency = budgetBreakdown?.otherCosts?.venue_currency || 'USD';

  // Calculate costs from budget breakdown (in event currency)
  // Memoize to recalculate when exchange rates or breakdown data changes
  const calculateCostsFromBreakdown = useMemo(() => {
    if (!budgetBreakdown || !fullBudget) return null;

    const breakdown = budgetBreakdown;
    const settings = fullBudget.settings;
    const nights = fullBudget?.nights ?? 0;
    const attendees = fullBudget?.attendees ?? groupSize;

    const result = calculateProposalBudget({ breakdown, settings, nights, attendees,
      eventCurrency, exchangeRates: normalizeBudgetRates(exchangeRates) });
    const { accommodation: accommodationTotal, activities: activitiesTotal, meals: mealsTotal,
      meetings: meetingsTotal, flightCost, groundTransport, miscellaneous: miscTotal,
      subtotal, contingencyAmount, planningOverheadAmount, profitMarginAmount,
      totalProgramCost, perAttendeeCost } = result;
    const rb = breakdown.roomBreakdown;
    const accommodationDetails = [
      rb && (rb.single_rooms ?? 0) > 0 && `${rb.single_rooms}x SGL @ ${formatCurrencyAmount(result.singleRoomRate, eventCurrency)}/night x ${result.roomNights} nights`,
      rb && (rb.double_rooms ?? 0) > 0 && `${rb.double_rooms}x DBL @ ${formatCurrencyAmount(result.doubleRoomRate, eventCurrency)}/night x ${result.roomNights} nights`,
    ].filter(Boolean).join(', ');
    return {
      accommodation: accommodationTotal,
      accommodationDetails,
      activities: activitiesTotal,
      meals: mealsTotal,
      meetings: meetingsTotal,
      flightCost,
      groundTransport,
      miscellaneous: miscTotal,
      subtotal,
      contingencyAmount,
      planningOverheadAmount,
      profitMarginAmount,
      totalProgramCost,
      perAttendeeCost,
      settings,
    };
  }, [budgetBreakdown, fullBudget, exchangeRates, eventCurrency, venueCurrency, groupSize, convertCurrency]);

  // Calculate pricing - use budget breakdown if available, otherwise fall back to destination content
  const calculatePricing = useMemo(() => {
    const budgetCosts = calculateCostsFromBreakdown;

    // If we have budget breakdown data, use it
    if (budgetCosts) {
      return {
        totalEstimated: budgetCosts.totalProgramCost,
        perPerson: budgetCosts.perAttendeeCost,
        breakdown: {
          accommodation: budgetCosts.accommodation,
          accommodationDetails: budgetCosts.accommodationDetails,
          flightCost: budgetCosts.flightCost,
          groundTransport: budgetCosts.groundTransport,
          meals: budgetCosts.meals,
          meetings: budgetCosts.meetings,
          activities: budgetCosts.activities,
          misc: budgetCosts.miscellaneous,
        },
        totals: {
          subtotal: budgetCosts.subtotal,
          contingency: budgetCosts.contingencyAmount,
          planningOverhead: budgetCosts.planningOverheadAmount,
          profitMargin: budgetCosts.profitMarginAmount,
          totalProgramCost: budgetCosts.totalProgramCost,
        },
        settings: budgetCosts.settings,
      };
    }

    // Fallback to old calculation from destination content
    let accommodation = null;
    const hotelIdToUse = selectedHotelForPricing || (hotels.length > 0 ? hotels[0].id : null);

    if (hotelIdToUse && venueData.length > 0) {
      const venueInfo = venueData.find(v => v.venue.id === hotelIdToUse);
      if (venueInfo) {
        const totalCost = venueInfo.shortlistItem.override_total_cost ?? venueInfo.shortlistItem.cvent_total_cost;
        if (totalCost !== null && totalCost !== undefined) {
          accommodation = Math.round(totalCost);
        }
      }
    }

    const flightCost = destinationContent?.flights_transportation !== null && destinationContent?.flights_transportation !== undefined
      ? Math.round(destinationContent.flights_transportation / 100)
      : null;

    const groundTransport = destinationContent?.ground_transport !== null && destinationContent?.ground_transport !== undefined
      ? Math.round(destinationContent.ground_transport / 100)
      : null;

    const meals = destinationContent?.meals_cost !== null && destinationContent?.meals_cost !== undefined
      ? Math.round(destinationContent.meals_cost / 100)
      : destinationContent?.food_beverage !== null && destinationContent?.food_beverage !== undefined
        ? Math.round(destinationContent.food_beverage / 100)
        : null;

    const meetings = destinationContent?.meetings_cost !== null && destinationContent?.meetings_cost !== undefined
      ? Math.round(destinationContent.meetings_cost / 100)
      : null;

    const activities = destinationContent?.activities_cost !== null && destinationContent?.activities_cost !== undefined
      ? Math.round(destinationContent.activities_cost / 100)
      : destinationContent?.activities_meetings !== null && destinationContent?.activities_meetings !== undefined
        ? Math.round(destinationContent.activities_meetings / 100)
        : null;

    const misc = destinationContent?.miscellaneous !== null && destinationContent?.miscellaneous !== undefined
      ? Math.round(destinationContent.miscellaneous / 100)
      : null;

    const hasRequiredFields = accommodation !== null && flightCost !== null && meals !== null;
    let totalEstimated: number | null = null;
    if (hasRequiredFields && accommodation !== null) {
      totalEstimated = accommodation + flightCost + meals;
      if (groundTransport !== null) totalEstimated += groundTransport;
      if (meetings !== null) totalEstimated += meetings;
      if (activities !== null) totalEstimated += activities;
      if (misc !== null) totalEstimated += misc;
    }

    const perPerson = totalEstimated !== null && groupSize > 0
      ? Math.round(totalEstimated / groupSize)
      : null;

    return {
      totalEstimated,
      perPerson,
      breakdown: {
        accommodation,
        accommodationDetails: null,
        flightCost,
        groundTransport,
        meals,
        meetings,
        activities,
        misc
      },
      totals: null,
      settings: null,
    };
  }, [calculateCostsFromBreakdown, destinationContent, groupSize, selectedHotelForPricing, hotels, venueData]);

  const pricing = calculatePricing;

  // Format currency for display. Delegates to the shared formatter so the
  // Destination and Comparison tabs show identical symbols (e.g. "US$" for
  // USD, "CA$" for CAD, "€" for EUR).
  const formatCurrency = (amount: number | null) =>
    formatCurrencyAmount(amount, eventCurrency || 'USD');

  return (
    <div className="h-full overflow-y-auto hide-scrollbar" style={{ backgroundColor: proposalTokens.colors.cream, color: proposalTokens.colors.teal }}>

      {/* 1. HERO SECTION WITH FLEXIBLE MEDIA */}
      <section id="overview" className="flex flex-col lg:flex-row">
        <div className="max-w-[1400px] mx-auto w-full flex flex-col lg:flex-row lg:items-stretch">
          {/* Left: Media Gallery (55% on desktop) - height driven by aspect ratios */}
          <div className="w-full lg:w-[55%] p-4 md:p-6 relative">
            <MediaGallery destination={destination} />

            {/* Floating Title over Gallery on Mobile - Only show if there's actual media */}
            {destination.media && destination.media.items && destination.media.items.length > 0 && (
              <div className="lg:hidden absolute bottom-8 left-8 z-10">
                <h1 className="text-4xl md:text-5xl font-serif font-bold text-white drop-shadow-[0_2px_8px_rgba(0,0,0,0.7)]" style={{ fontFamily: proposalTokens.typography.fontFamily.serif.join(', ') }}>
                  {destination.city}
                </h1>
              </div>
            )}
          </div>

          {/* Right: Content (45% on desktop) - starts from top */}
          <div className="w-full lg:w-[45%] p-6 md:p-10 lg:p-12 flex flex-col justify-start" style={{ backgroundColor: proposalTokens.colors.cream }}>
            {/* Header - Desktop only */}
            <div className="hidden lg:block mb-0">
              <p className="font-bold tracking-widest uppercase text-sm mb-2" style={{ color: proposalTokens.colors.teal, fontFamily: proposalTokens.typography.fontFamily.sans.join(', ') }}>
                {destination.country}
              </p>
              <h1 className="text-5xl xl:text-6xl font-serif font-medium mb-4" style={{ color: proposalTokens.colors.teal, fontFamily: proposalTokens.typography.fontFamily.serif.join(', ') }}>
                {destination.city}
              </h1>
              <div className="h-1 w-20 mb-4" style={{ backgroundColor: proposalTokens.colors.gold }}></div>

              {/* Description - Below title */}
              {description && (
                <p className="text-lg xl:text-xl leading-relaxed mb-6" style={{ color: `${proposalTokens.colors.teal}b3`, fontFamily: proposalTokens.typography.fontFamily.sans.join(', ') }}>
                  {description}
                </p>
              )}
            </div>

            {/* Description - Mobile only (shown below floating title) */}
            <div className="lg:hidden mb-6">
              {description && (
                <p className="text-lg leading-relaxed" style={{ color: `${proposalTokens.colors.teal}b3`, fontFamily: proposalTokens.typography.fontFamily.sans.join(', ') }}>
                  {description}
                </p>
              )}
            </div>

            {/* Quick Facts - Climate Widgets */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              {/* Day Avg */}
              <div className="flex flex-col items-center justify-center p-3 bg-white rounded-xl border border-black/10 shadow-sm text-center">
                <Sun className="w-5 h-5 text-gold mb-1" />
                <p className="text-[10px] font-bold text-teal/40 uppercase">Day Avg</p>
                <p className="text-teal font-bold text-lg">
                  {weatherData?.day_avg_temperature !== null && weatherData?.day_avg_temperature !== undefined
                    ? `${Math.round(weatherData.day_avg_temperature)}${tempUnitSymbol}`
                    : `${Math.round(convertTemperature(22, temperatureUnit) ?? 22)}${tempUnitSymbol}`}
                </p>
              </div>

              {/* Night Avg */}
              <div className="flex flex-col items-center justify-center p-3 bg-white rounded-xl border border-black/10 shadow-sm text-center">
                <Moon className="w-5 h-5 text-teal/60 mb-1" />
                <p className="text-[10px] font-bold text-teal/40 uppercase">Night Avg</p>
                <p className="text-teal font-bold text-lg">
                  {weatherData?.night_avg_temperature !== null && weatherData?.night_avg_temperature !== undefined
                    ? `${Math.round(weatherData.night_avg_temperature)}${tempUnitSymbol}`
                    : `${Math.round(convertTemperature(13, temperatureUnit) ?? 13)}${tempUnitSymbol}`}
                </p>
              </div>

              {/* Rainy Days */}
              <div className="flex flex-col items-center justify-center p-3 bg-white rounded-xl border border-black/10 shadow-sm text-center">
                <CloudRain className="w-5 h-5 text-teal/60 mb-1" />
                <p className="text-[10px] font-bold text-teal/40 uppercase">Rainy Days</p>
                <p className="text-teal font-bold text-lg">{weatherData?.rainy_days ?? 7}</p>
              </div>

              {/* View More */}
              <button
                onClick={() => setIsClimateModalOpen(true)}
                className="flex flex-col items-center justify-center p-3 bg-teal text-white rounded-xl shadow-md hover:bg-teal/90 transition-colors cursor-pointer group"
              >
                <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center mb-1 group-hover:bg-white/20 transition-colors">
                  <ArrowRight className="w-4 h-4 text-white" />
                </div>
                <p className="text-[10px] font-bold text-white/60 uppercase">Full Report</p>
                <p className="text-white font-bold text-sm">View More</p>
              </button>
            </div>

            {/* Climate Report Modal */}
            <ClimateReportModal
              isOpen={isClimateModalOpen}
              onClose={() => setIsClimateModalOpen(false)}
              destinationName={destination.city}
              travelMonth={travelMonth}
              weatherData={weatherData}
              temperatureUnit={temperatureUnit}
            />

            {/* PROS SECTION - Below weather widgets */}
            <ProsSection pros={pros} destinationName={destination.city} />

            {/* Action Buttons */}
            <div className="flex gap-4 mt-8">
              <button
                onClick={() => scrollTo('hotels')}
                className="flex-1 py-4 font-bold rounded-md hover:opacity-90 transition-colors flex items-center justify-center"
                style={{ backgroundColor: proposalTokens.colors.gold, color: '#000000' }}
              >
                Explore Hotels
              </button>
              {showBudget && <button
                onClick={() => scrollTo('pricing')}
                className="flex-1 py-4 bg-transparent border border-black/20 font-bold rounded-md hover:bg-black/5 transition-colors"
                style={{ color: proposalTokens.colors.teal }}
              >
                View Costs
              </button>}
            </div>
          </div>
        </div>
      </section>

      {/* 2. WHERE TO STAY - 2 Column Grid (White Background) */}
      <section id="hotels" className="py-12 md:py-24 px-6 md:px-12 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="mb-12">
            <h2 className="text-4xl font-serif font-bold mb-2" style={{ color: proposalTokens.colors.teal, fontFamily: proposalTokens.typography.fontFamily.serif.join(', ') }}>
              Where to Stay
            </h2>
            <p style={{ color: `${proposalTokens.colors.teal}99` }}>Handpicked accommodations for your team.</p>
          </div>

          {hotels.length > 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {hotels.map((hotel) => (
                <div key={hotel.id} className="rounded-2xl overflow-hidden border border-black/10 shadow-lg hover:shadow-xl transition-shadow group" style={{ backgroundColor: proposalTokens.colors.cream }}>
                  {/* Image */}
                  <div className="relative aspect-square min-[480px]:aspect-video w-full overflow-hidden">
                    <img src={hotel.image} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" alt={hotel.name} loading="lazy" />

                    {(hotel.recommended || hotel.buyout) && (
                      <div className="absolute top-4 right-4 z-20 flex flex-col items-end gap-2 drop-shadow-lg pointer-events-none">
                        {hotel.recommended && (
                          <div
                            className="px-3 py-1.5 flex items-center gap-2 rounded-md shadow-sm"
                            style={{
                              background: 'linear-gradient(to right, #f3ad0f, #f437a8)',
                            }}
                          >
                            <img
                              src="/images/moniker_logo_mark.svg"
                              alt="Moniker Approved"
                              className="w-4 h-4 object-contain brightness-0 invert"
                            />
                            <span
                              className="text-xs font-bold uppercase tracking-widest text-white"
                              style={{ fontFamily: proposalTokens.typography.fontFamily.sans.join(', ') }}
                            >
                              Recommended
                            </span>
                          </div>
                        )}
                        {hotel.buyout && (
                          <div
                            className="px-3 py-1.5 flex items-center gap-2 rounded-md shadow-sm"
                            style={{
                              background: 'linear-gradient(to right, #f3ad0f, #f97316)',
                            }}
                          >
                            <Check className="w-4 h-4 text-white" />
                            <span
                              className="text-xs font-bold uppercase tracking-widest text-white"
                              style={{ fontFamily: proposalTokens.typography.fontFamily.sans.join(', ') }}
                            >
                              Buyout
                            </span>
                          </div>
                        )}
                      </div>
                    )}

                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
                    <div className="absolute bottom-0 left-0 right-0 p-6">
                      <div className="flex items-center mb-2">
                        <StarRating rating={hotel.stars} size="sm" showEmptyStars={true} goldColor={proposalTokens.colors.gold} />
                      </div>
                      <div className="flex flex-wrap items-center gap-2 mb-3">
                        <h3 className="text-2xl font-serif font-bold text-white" style={{ fontFamily: proposalTokens.typography.fontFamily.serif.join(', ') }}>
                          {hotel.name}
                        </h3>
                        {hotel.allInclusive && (
                          <span
                            className="inline-flex items-center gap-1.5 rounded-full bg-white/95 px-3 py-1 text-xs font-bold uppercase tracking-wider shadow-sm"
                            style={{
                              color: proposalTokens.colors.teal,
                              fontFamily: proposalTokens.typography.fontFamily.sans.join(', '),
                            }}
                          >
                            <Utensils className="h-3.5 w-3.5" />
                            All-Inclusive
                          </span>
                        )}
                      </div>
                      {hotel.city && hotel.city !== destination.city && (
                        <p className="text-xs text-white/70 uppercase tracking-wider mb-2">{hotel.city}</p>
                      )}

                      <div className="flex flex-wrap gap-x-4 gap-y-1 mb-4">
                        {hotel.roomCount && (
                          <span className="flex items-center text-sm text-white/80">
                            <Check className="w-4 h-4 mr-1 text-white" /> {hotel.roomCount} Rooms
                          </span>
                        )}
                        {hotel.meetingSpaces && hotel.meetingSpaces.length > 0 && (
                          <span className="flex items-center text-sm text-white/80">
                            <Check className="w-4 h-4 mr-1 text-white" /> {hotel.meetingSpaces.length} Meeting Spaces
                          </span>
                        )}
                        {hotel.availableDates && (
                          <span className="flex items-center text-sm text-white/70">
                            <CalendarIcon className="w-3.5 h-3.5 mr-1 text-white/60" />
                            {(() => {
                              const start = parseDateOnly(hotel.availableDates.start) || new Date(hotel.availableDates.start);
                              const end = parseDateOnly(hotel.availableDates.end) || new Date(hotel.availableDates.end);
                              const fmt = (d: Date, includeYear: boolean) =>
                                d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', ...(includeYear ? { year: 'numeric' } : {}) });
                              const sameYear = start.getFullYear() === end.getFullYear();
                              return `${fmt(start, !sameYear)} – ${fmt(end, true)}`;
                            })()}
                          </span>
                        )}
                      </div>
                      <div className="flex justify-between items-center pt-4 border-t border-white/20">
                        <div>
                          <p className="text-xs text-white/60 uppercase">From</p>
                          <p className="text-lg font-bold text-white">{hotel.priceRange || 'Contact for pricing'}</p>
                        </div>
                        <button
                          onClick={() => {
                            setSelectedHotel(hotel);
                            // Find matching venue data
                            const venueInfo = venueData.find(v => v.venue.id === hotel.id);
                            setSelectedVenueData(venueInfo || null);
                            onViewHotelDetails?.(hotel.id);
                          }}
                          className="px-6 py-2 text-black text-sm font-bold rounded-md hover:opacity-90 transition-colors"
                          style={{ backgroundColor: proposalTokens.colors.gold }}
                        >
                          View Details
                        </button>
                      </div>
                    </div>
                  </div>
                  {hotel.clientNote && (
                    <div className="px-6 py-4 border-t border-black/5" data-testid="client-note">
                      <p className="text-xs uppercase font-bold tracking-wider mb-1" style={{ color: `${proposalTokens.colors.teal}99` }}>
                        Moniker's notes
                      </p>
                      <p className="text-sm whitespace-pre-wrap leading-relaxed" style={{ color: proposalTokens.colors.teal }}>
                        {hotel.clientNote}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <p style={{ color: `${proposalTokens.colors.teal}99` }}>Hotel information will be available here.</p>
            </div>
          )}
        </div>
      </section>

      {/* Hotel Details Modal */}
      <HotelDetailsModal
        isOpen={selectedHotel !== null && selectedVenueData !== null}
        onClose={() => {
          setSelectedHotel(null);
          setSelectedVenueData(null);
        }}
        venue={selectedVenueData?.venue || null}
        shortlistItem={selectedVenueData?.shortlistItem || null}
        destinationName={destination.city}
        destinationCountry={destination.country}
        destinationId={destination.id}
        shareLinkToken={shareLinkToken}
        sessionId={sessionId}
        onAddToFavorites={onAddToFavorites}
        isInFavorites={selectedHotel ? shortlistedHotelIds?.has(selectedHotel.id) || false : false}
        eventDateType={eventDateType}
        eventMonth={eventMonth}
        eventStartDate={eventStartDate}
        eventEndDate={eventEndDate}
        eventDateDisplayOverride={eventDateDisplayOverride}
        distanceUnit={distanceUnit}
      />

      {/* Activity Details Modal */}
      <ActivityDetailsModal
        isOpen={selectedActivity !== null}
        onClose={() => setSelectedActivity(null)}
        activity={selectedActivity}
      />

      {/* Event Details Modal */}
      <EventDetailsModal
        isOpen={selectedEvent !== null}
        onClose={() => setSelectedEvent(null)}
        event={selectedEvent}
      />

      {/* 3. CURATED EXPERIENCES + EVENTS - Only show if we have activities or events */}
      {(activities.length > 0 || events.length > 0) && (
        <>
          {activities.length > 0 && (
      <section id="experiences" className={cn("overflow-hidden", events.length > 0 ? "pt-12 md:pt-24 pb-0" : "py-12 md:py-24")} style={{ backgroundColor: proposalTokens.colors.cream }}>
        <div className="max-w-[1400px] mx-auto relative px-6">
          <div className="mb-12 flex justify-between items-end">
            <div>
              <h2 className="text-4xl font-serif font-bold mb-2" style={{ color: proposalTokens.colors.teal, fontFamily: proposalTokens.typography.fontFamily.serif.join(', ') }}>
                Curated Experiences
              </h2>
              <p style={{ color: `${proposalTokens.colors.teal}b3` }}>Handpicked activities and adventures for your team.</p>
            </div>
            {activities.length > 1 && (
              <div className="flex gap-3">
                <button
                  onClick={() => scrollExperiences('left')}
                  disabled={!canScrollLeft}
                  className={cn(
                    "w-12 h-12 rounded-md border flex items-center justify-center transition-all",
                    canScrollLeft
                      ? "hover:bg-teal hover:text-white cursor-pointer"
                      : "opacity-40 cursor-not-allowed"
                  )}
                  style={{
                    borderColor: `${proposalTokens.colors.teal}4d`,
                    color: proposalTokens.colors.teal
                  }}
                  onMouseEnter={(e) => {
                    if (canScrollLeft) {
                      e.currentTarget.style.backgroundColor = proposalTokens.colors.teal;
                      e.currentTarget.style.color = 'white';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = proposalTokens.colors.teal;
                  }}
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>
                <button
                  onClick={() => scrollExperiences('right')}
                  disabled={!canScrollRight}
                  className={cn(
                    "w-12 h-12 rounded-md border flex items-center justify-center transition-all",
                    canScrollRight
                      ? "hover:bg-teal hover:text-white cursor-pointer"
                      : "opacity-40 cursor-not-allowed"
                  )}
                  style={{
                    borderColor: `${proposalTokens.colors.teal}4d`,
                    color: proposalTokens.colors.teal
                  }}
                  onMouseEnter={(e) => {
                    if (canScrollRight) {
                      e.currentTarget.style.backgroundColor = proposalTokens.colors.teal;
                      e.currentTarget.style.color = 'white';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = proposalTokens.colors.teal;
                  }}
                >
                  <ChevronRight className="w-6 h-6" />
                </button>
              </div>
            )}
          </div>

          <div className="relative">
              <div
                ref={experienceScrollRef}
                className="flex overflow-x-auto snap-x snap-mandatory gap-6 pb-8 hide-scrollbar relative z-0"
                style={{ scrollBehavior: 'smooth' }}
              >
                {activities.map((activity) => {
                  const { firstSentence } = getFirstSentence(activity.description || '');
                  return (
                    <div
                      key={activity.id}
                      onClick={() => setSelectedActivity(activity)}
                      className="snap-center shrink-0 w-[280px] md:w-[320px] relative rounded-2xl overflow-hidden group cursor-pointer bg-white shadow-lg hover:shadow-xl transition-shadow flex flex-col"
                    >
                      {/* Image */}
                      <div className="relative h-[200px] w-full overflow-hidden">
                        <img src={activity.image} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" alt={activity.title} loading="lazy" />
                        <div className="absolute top-4 left-4">
                          <span className="text-black text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider" style={{ backgroundColor: proposalTokens.colors.gold }}>
                            {activity.category}
                          </span>
                        </div>
                      </div>
                      {/* Details */}
                      <div className="p-5 flex-1 flex flex-col">
                        <h3 className="text-lg font-serif font-bold mb-2" style={{ color: proposalTokens.colors.teal, fontFamily: proposalTokens.typography.fontFamily.serif.join(', ') }}>
                          {activity.title}
                        </h3>
                        {activity.description && (
                          <div className="mb-4 flex-1">
                            <p className="text-sm" style={{ color: `${proposalTokens.colors.teal}99` }}>
                              {firstSentence}
                            </p>
                          </div>
                        )}
                        <div className="flex justify-between items-center pt-3 border-t border-black/10">
                          {activity.duration && (
                            <span className="flex items-center text-sm" style={{ color: `${proposalTokens.colors.teal}80` }}>
                              <Clock className="w-4 h-4 mr-1" /> {activity.duration}
                            </span>
                          )}
                          {activity.intensity && (
                            <span className={cn(
                              "text-xs font-bold uppercase px-2 py-1 rounded",
                              activity.intensity === 'Active'
                                ? 'bg-red-100 text-red-600'
                                : activity.intensity === 'Moderate'
                                ? 'text-black'
                                : 'text-teal'
                            )}
                            style={activity.intensity === 'Moderate' ? { backgroundColor: `${proposalTokens.colors.gold}33` } : activity.intensity === 'Relaxed' ? { backgroundColor: `${proposalTokens.colors.teal}1a`, color: proposalTokens.colors.teal } : undefined}
                            >
                              {activity.intensity}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
        </div>
      </section>
          )}

      {/* 3B. EVENTS - Horizontal Scroll (below Curated Experiences) */}
      {events.length > 0 && (
        <section id="destination-events" className={cn("overflow-hidden", activities.length > 0 ? "pt-6 md:pt-8 pb-12 md:pb-24" : "py-12 md:py-24")} style={{ backgroundColor: proposalTokens.colors.cream }}>
          <div className="max-w-[1400px] mx-auto relative px-6">
            <div className="mb-12 flex justify-between items-end">
              <div>
                <h2 className="text-4xl font-serif font-bold mb-2" style={{ color: proposalTokens.colors.teal, fontFamily: proposalTokens.typography.fontFamily.serif.join(', ') }}>
                  Events
                </h2>
                <p style={{ color: `${proposalTokens.colors.teal}b3` }}>Special occasions and gatherings planned for your team.</p>
              </div>
              {events.length > 1 && (
                <div className="flex gap-3">
                  <button
                    onClick={() => scrollEvents('left')}
                    disabled={!canScrollEventsLeft}
                    className={cn(
                      "w-12 h-12 rounded-md border flex items-center justify-center transition-all",
                      canScrollEventsLeft
                        ? "hover:bg-teal hover:text-white cursor-pointer"
                        : "opacity-40 cursor-not-allowed"
                    )}
                    style={{
                      borderColor: `${proposalTokens.colors.teal}4d`,
                      color: proposalTokens.colors.teal
                    }}
                    onMouseEnter={(e) => {
                      if (canScrollEventsLeft) {
                        e.currentTarget.style.backgroundColor = proposalTokens.colors.teal;
                        e.currentTarget.style.color = 'white';
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.color = proposalTokens.colors.teal;
                    }}
                  >
                    <ChevronLeft className="w-6 h-6" />
                  </button>
                  <button
                    onClick={() => scrollEvents('right')}
                    disabled={!canScrollEventsRight}
                    className={cn(
                      "w-12 h-12 rounded-md border flex items-center justify-center transition-all",
                      canScrollEventsRight
                        ? "hover:bg-teal hover:text-white cursor-pointer"
                        : "opacity-40 cursor-not-allowed"
                    )}
                    style={{
                      borderColor: `${proposalTokens.colors.teal}4d`,
                      color: proposalTokens.colors.teal
                    }}
                    onMouseEnter={(e) => {
                      if (canScrollEventsRight) {
                        e.currentTarget.style.backgroundColor = proposalTokens.colors.teal;
                        e.currentTarget.style.color = 'white';
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.color = proposalTokens.colors.teal;
                    }}
                  >
                    <ChevronRight className="w-6 h-6" />
                  </button>
                </div>
              )}
            </div>

            <div className="relative">
              <div
                ref={eventScrollRef}
                className="flex overflow-x-auto snap-x snap-mandatory gap-6 pb-8 hide-scrollbar relative z-0"
                style={{ scrollBehavior: 'smooth' }}
              >
                {events.map((event) => (
                  <div
                    key={event.id}
                    onClick={() => setSelectedEvent(event)}
                    className="snap-center shrink-0 w-[280px] md:w-[320px] relative rounded-2xl overflow-hidden group cursor-pointer bg-white shadow-lg hover:shadow-xl transition-shadow flex flex-col"
                  >
                    {/* Image */}
                    <div className="relative h-[200px] w-full overflow-hidden">
                      <img src={event.image} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" alt={event.title} loading="lazy" />
                      <div className="absolute top-4 left-4">
                        <span className="text-black text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider" style={{ backgroundColor: proposalTokens.colors.gold }}>
                          {event.tag}
                        </span>
                      </div>
                    </div>
                    {/* Details */}
                    <div className="p-5 flex-1 flex flex-col">
                      <h3 className="text-lg font-serif font-bold mb-2" style={{ color: proposalTokens.colors.teal, fontFamily: proposalTokens.typography.fontFamily.serif.join(', ') }}>
                        {event.title}
                      </h3>
                      {event.description && (
                        <div className="mb-4 flex-1">
                          <p className="text-sm" style={{ color: `${proposalTokens.colors.teal}99` }}>
                            {getFirstSentence(event.description).firstSentence}
                          </p>
                        </div>
                      )}
                      <div className="flex justify-between items-center pt-3 border-t border-black/10">
                        {/* Left: Participants */}
                        {event.participants && (
                          <span className="flex items-center text-sm" style={{ color: `${proposalTokens.colors.teal}80` }}>
                            <Users className="w-4 h-4 mr-1" /> {event.participants}
                          </span>
                        )}
                        {/* Right: Price */}
                        {event.pricePerPerson && (
                          <span className="text-sm font-medium" style={{ color: proposalTokens.colors.teal }}>
                            ${event.pricePerPerson}/person
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}
        </>
      )}

      {/* 4. PRICING - Detailed Accounting Breakdown (hidden when the team turns the budget off) */}
      {showBudget && (
      <section id="pricing" className="py-24 px-6 md:px-12" style={{ backgroundColor: proposalTokens.colors.teal }}>
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <h2 className="text-4xl font-serif font-bold text-white mb-2" style={{ fontFamily: proposalTokens.typography.fontFamily.serif.join(', ') }}>
              Budget Estimate
            </h2>
            <p className="text-white/70 text-sm max-w-3xl mx-auto leading-relaxed mb-6">
              The budget estimates are based on the information we have gathered thus far. They include the quoted accommodation, meeting room and meal costs. The transport, AV, flights, activities and miscellaneous are estimates based on our experience in the destination. Once the program is confirmed, a more detailed budget will be created.
            </p>

            {calculateCostsFromBreakdown && !Number.isFinite(calculateCostsFromBreakdown.totalProgramCost) && (
              <p role="status" className="mb-4 rounded-lg bg-amber-50 p-3 text-sm text-amber-900">{BUDGET_FX_UNAVAILABLE}</p>
            )}
            {/* Hotel Picker */}
            {hotels.length > 0 && (
              <div className="flex items-center justify-center gap-4 mb-8">
                <label className="text-white/80 text-sm font-medium">Select Hotel:</label>
                <select
                  value={selectedHotelForPricing || hotels[0].id || ''}
                  onChange={(e) => setSelectedHotelForPricing(e.target.value)}
                  className="px-4 py-2 rounded-lg text-black font-bold focus:outline-none min-w-[250px] cursor-pointer"
                  style={{ backgroundColor: proposalTokens.colors.gold, border: 'none' }}
                >
                  {hotels.map(hotel => (
                    <option key={hotel.id} value={hotel.id} style={{ backgroundColor: '#1e293b', color: '#fff' }}>
                      {hotel.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* Budget Card */}
          <div className="relative rounded-[2rem] overflow-hidden shadow-2xl border" style={{ background: 'linear-gradient(to bottom right, #000000, #1e293b)', borderColor: proposalTokens.colors.gold }}>
            {/* Background Logo Watermark */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-[0.03]">
              <img src="/moniker_logo_mark.svg" alt="" className="w-full h-full object-cover" />
            </div>

            <div className="relative z-10">
              <div className="p-6 md:p-8">
                {/* Header Section of Card */}
                <div className="flex items-center gap-3 mb-5 pb-5 border-b border-white/10">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center border" style={{ backgroundColor: `${proposalTokens.colors.gold}1a`, borderColor: `${proposalTokens.colors.gold}33` }}>
                    <CircleDollarSign className="w-6 h-6" style={{ color: proposalTokens.colors.gold }} />
                  </div>
                  <div>
                    <h3 className="text-xl font-serif font-bold text-white" style={{ fontFamily: proposalTokens.typography.fontFamily.serif.join(', ') }}>
                      Estimated Budget
                    </h3>
                    <p className="text-white/50 text-sm">Comprehensive breakdown including all fees</p>
                  </div>
                </div>

                {/* Line Items */}
                <div className="space-y-1">
                  {/* Accommodation */}
                  {(() => {
                    const accommodationPercent = pricing.totalEstimated && pricing.breakdown.accommodation !== null
                      ? Math.round((pricing.breakdown.accommodation / (pricing.totals?.subtotal || pricing.totalEstimated)) * 100)
                      : null;

                    return (
                      <div className="py-2.5 border-b border-white/10">
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                          <div className="flex items-start gap-3 flex-1 min-w-0">
                            <div className="w-8 h-8 flex-shrink-0 rounded-lg flex items-center justify-center mt-0.5" style={{ backgroundColor: `${proposalTokens.colors.gold}33` }}>
                              <Building2 className="w-4 h-4" style={{ color: proposalTokens.colors.gold }} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-white font-semibold mb-1">Accommodation</p>
                              {pricing.breakdown.accommodationDetails ? (
                                <p className="text-white/60 text-xs leading-relaxed">{pricing.breakdown.accommodationDetails}</p>
                              ) : (
                                <p className="text-white/50 text-sm">Room rates and nights</p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center justify-between md:justify-end md:gap-6 md:text-right">
                            {accommodationPercent !== null ? (
                              <span className="text-white/50 text-sm">{accommodationPercent}%</span>
                            ) : null}
                            <span className="text-white text-lg md:min-w-[100px] md:text-right" style={{ fontFamily: proposalTokens.typography.fontFamily.sans.join(', ') }}>
                              {pricing.breakdown.accommodation !== null ? formatCurrency(pricing.breakdown.accommodation) : '—'}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Flight Cost */}
                  {(() => {
                    const flightPercent = pricing.totalEstimated && pricing.breakdown.flightCost !== null
                      ? Math.round((pricing.breakdown.flightCost / pricing.totalEstimated) * 100)
                      : null;
                    const perPerson = pricing.breakdown.flightCost !== null && groupSize > 0
                      ? Math.round(pricing.breakdown.flightCost / groupSize)
                      : null;

                    return (
                      <div className="py-2.5 border-b border-white/10">
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 flex-shrink-0 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${proposalTokens.colors.gold}33` }}>
                              <Plane className="w-4 h-4" style={{ color: proposalTokens.colors.gold }} />
                            </div>
                            <div>
                              <p className="text-white font-semibold">Flight Cost</p>
                              {perPerson !== null ? (
                                <p className="text-white/50 text-sm">{groupSize} attendees × {formatCurrency(perPerson)} per person</p>
                              ) : null}
                            </div>
                          </div>
                          <div className="flex items-center justify-between md:justify-end md:gap-6 md:text-right">
                            {flightPercent !== null ? (
                              <span className="text-white/50 text-sm">{flightPercent}%</span>
                            ) : null}
                            <span className="text-white text-lg md:min-w-[100px] md:text-right" style={{ fontFamily: proposalTokens.typography.fontFamily.sans.join(', ') }}>
                              {pricing.breakdown.flightCost !== null ? formatCurrency(pricing.breakdown.flightCost) : '—'}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Ground Transport - only show if set */}
                  {pricing.breakdown.groundTransport !== null && (
                    (() => {
                      const groundPercent = pricing.totalEstimated
                        ? Math.round((pricing.breakdown.groundTransport! / pricing.totalEstimated) * 100)
                        : null;

                      return (
                        <div className="py-2.5 border-b border-white/10">
                          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 flex-shrink-0 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${proposalTokens.colors.gold}33` }}>
                                <Car className="w-4 h-4" style={{ color: proposalTokens.colors.gold }} />
                              </div>
                              <div>
                                <p className="text-white font-semibold">Ground Transport</p>
                                <p className="text-white/50 text-sm">Airport transfers & local transport</p>
                              </div>
                            </div>
                            <div className="flex items-center justify-between md:justify-end md:gap-6 md:text-right">
                              {groundPercent !== null ? (
                                <span className="text-white/50 text-sm">{groundPercent}%</span>
                              ) : null}
                              <span className="text-white text-lg md:min-w-[100px] md:text-right" style={{ fontFamily: proposalTokens.typography.fontFamily.sans.join(', ') }}>
                                {formatCurrency(pricing.breakdown.groundTransport!)}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })()
                  )}

                  {/* Meals */}
                  {(() => {
                    const mealsPercent = pricing.totalEstimated && pricing.breakdown.meals !== null
                      ? Math.round((pricing.breakdown.meals / (pricing.totals?.subtotal || pricing.totalEstimated)) * 100)
                      : null;
                    const isExpanded = expandedSections.meals;
                    const hasBreakdown = budgetBreakdown?.mealsBreakdown && (
                      budgetBreakdown.mealsBreakdown.breakfast_count > 0 ||
                      budgetBreakdown.mealsBreakdown.lunch_count > 0 ||
                      budgetBreakdown.mealsBreakdown.dinner_count > 0 ||
                      budgetBreakdown.mealsBreakdown.alcohol_count > 0
                    );

                    return (
                      <div className="border-b border-white/10">
                        <div
                          className={`py-2.5 cursor-pointer transition-colors ${hasBreakdown ? 'hover:bg-white/5' : ''}`}
                          onClick={() => hasBreakdown && toggleSection('meals')}
                        >
                          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                            <div className="flex items-center gap-3 flex-1">
                              <div className="w-8 h-8 flex-shrink-0 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${proposalTokens.colors.gold}33` }}>
                                <Utensils className="w-4 h-4" style={{ color: proposalTokens.colors.gold }} />
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <p className="text-white font-semibold">Meals</p>
                                  {hasBreakdown && (
                                    <button
                                      className="text-white/50 hover:text-white transition-colors"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        toggleSection('meals');
                                      }}
                                    >
                                      {isExpanded ? (
                                        <ChevronUp className="w-4 h-4" />
                                      ) : (
                                        <ChevronDown className="w-4 h-4" />
                                      )}
                                    </button>
                                  )}
                                </div>
                                <p className="text-white/50 text-sm">Breakfast, Lunch, Dinner & Drinks</p>
                              </div>
                            </div>
                            <div className="flex items-center justify-between md:justify-end md:gap-6 md:text-right">
                              {mealsPercent !== null ? (
                                <span className="text-white/50 text-sm">{mealsPercent}%</span>
                              ) : null}
                              <span className="text-white text-lg md:min-w-[100px] md:text-right" style={{ fontFamily: proposalTokens.typography.fontFamily.sans.join(', ') }}>
                                {pricing.breakdown.meals !== null ? formatCurrency(pricing.breakdown.meals) : '—'}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Meals Breakdown */}
                        {isExpanded && hasBreakdown && budgetBreakdown?.mealsBreakdown && (() => {
                          const mb = budgetBreakdown.mealsBreakdown;
                          const attendees = fullBudget?.attendees ?? groupSize;
                          const convertCentsToDollars = (cents: number | null, fromCurrency: string = venueCurrency): number => {
                            if (cents === null) return 0;
                            let dollars = cents / 100;
                            if (fromCurrency !== eventCurrency) {
                              dollars = convertCurrency(dollars, fromCurrency, eventCurrency);
                            }
                            return dollars;
                          };

                          const breakfastCost = convertCentsToDollars(mb.breakfast_cost_per_attendee, venueCurrency);
                          const breakfastTotal = breakfastCost * mb.breakfast_count * attendees;
                          const lunchCost = convertCentsToDollars(mb.lunch_cost_per_attendee, venueCurrency);
                          const lunchTotal = lunchCost * mb.lunch_count * attendees;
                          const dinnerCost = convertCentsToDollars(mb.dinner_cost_per_attendee, venueCurrency);
                          const dinnerTotal = dinnerCost * mb.dinner_count * attendees;
                          const alcoholCost = convertCentsToDollars(mb.alcohol_cost_per_attendee, venueCurrency);
                          const alcoholTotal = alcoholCost * mb.alcohol_count * attendees;

                          return (
                            <div className="pb-4 pl-14 space-y-2 border-t border-white/5 pt-3">
                              {mb.breakfast_count > 0 && (
                                <div className="flex justify-between items-center text-sm">
                                  <span className="text-white/70">Breakfast ({mb.breakfast_count}x)</span>
                                  <span className="text-white/80 font-medium">{formatCurrency(breakfastTotal)}</span>
                                </div>
                              )}
                              {mb.lunch_count > 0 && (
                                <div className="flex justify-between items-center text-sm">
                                  <span className="text-white/70">Lunch ({mb.lunch_count}x)</span>
                                  <span className="text-white/80 font-medium">{formatCurrency(lunchTotal)}</span>
                                </div>
                              )}
                              {mb.dinner_count > 0 && (
                                <div className="flex justify-between items-center text-sm">
                                  <span className="text-white/70">Dinner ({mb.dinner_count}x)</span>
                                  <span className="text-white/80 font-medium">{formatCurrency(dinnerTotal)}</span>
                                </div>
                              )}
                              {mb.alcohol_count > 0 && (
                                <div className="flex justify-between items-center text-sm">
                                  <span className="text-white/70">Drinks ({mb.alcohol_count}x)</span>
                                  <span className="text-white/80 font-medium">{formatCurrency(alcoholTotal)}</span>
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    );
                  })()}

                  {/* Meetings - only show if set */}
                  {pricing.breakdown.meetings !== null && (
                    (() => {
                      const meetingsPercent = pricing.totalEstimated
                        ? Math.round((pricing.breakdown.meetings! / (pricing.totals?.subtotal || pricing.totalEstimated)) * 100)
                        : null;
                      const isExpanded = expandedSections.meetings;
                      const hasBreakdown = budgetBreakdown?.meetingsBreakdown && (
                        budgetBreakdown.meetingsBreakdown.number_of_meetings > 0 ||
                        budgetBreakdown.meetingsBreakdown.coffee_count > 0 ||
                        budgetBreakdown.meetingsBreakdown.meeting_room_rental !== null ||
                        budgetBreakdown.meetingsBreakdown.meeting_setup_fees !== null ||
                        budgetBreakdown.meetingsBreakdown.meeting_av_cost !== null
                      );

                      return (
                        <div className="border-b border-white/10">
                          <div
                            className={`py-2.5 cursor-pointer transition-colors ${hasBreakdown ? 'hover:bg-white/5' : ''}`}
                            onClick={() => hasBreakdown && toggleSection('meetings')}
                          >
                            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                              <div className="flex items-center gap-3 flex-1">
                                <div className="w-8 h-8 flex-shrink-0 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${proposalTokens.colors.gold}33` }}>
                                  <CalendarIcon className="w-4 h-4" style={{ color: proposalTokens.colors.gold }} />
                                </div>
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <p className="text-white font-semibold">Meetings</p>
                                    {hasBreakdown && (
                                      <button
                                        className="text-white/50 hover:text-white transition-colors"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          toggleSection('meetings');
                                        }}
                                      >
                                        {isExpanded ? (
                                          <ChevronUp className="w-4 h-4" />
                                        ) : (
                                          <ChevronDown className="w-4 h-4" />
                                        )}
                                      </button>
                                    )}
                                  </div>
                                  <p className="text-white/50 text-sm">Meeting spaces & AV equipment</p>
                                </div>
                              </div>
                              <div className="flex items-center justify-between md:justify-end md:gap-6 md:text-right">
                                {meetingsPercent !== null ? (
                                  <span className="text-white/50 text-sm">{meetingsPercent}%</span>
                                ) : null}
                                <span className="text-white text-lg md:min-w-[100px] md:text-right" style={{ fontFamily: proposalTokens.typography.fontFamily.sans.join(', ') }}>
                                  {formatCurrency(pricing.breakdown.meetings!)}
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* Meetings Breakdown */}
                          {isExpanded && hasBreakdown && budgetBreakdown?.meetingsBreakdown && (() => {
                            const mb = budgetBreakdown.meetingsBreakdown;
                            const attendees = fullBudget?.attendees ?? groupSize;
                            const convertCentsToDollars = (cents: number | null, fromCurrency: string = venueCurrency): number => {
                              if (cents === null) return 0;
                              let dollars = cents / 100;
                              if (fromCurrency !== eventCurrency) {
                                dollars = convertCurrency(dollars, fromCurrency, eventCurrency);
                              }
                              return dollars;
                            };

                            const roomRental = convertCentsToDollars(mb.meeting_room_rental, venueCurrency);
                            const setupFees = convertCentsToDollars(mb.meeting_setup_fees, venueCurrency);
                            const avCost = convertCentsToDollars(mb.meeting_av_cost, venueCurrency);
                            const numberOfMeetings = mb.number_of_meetings || 0;
                            const perMeetingTotal = (roomRental + setupFees + avCost) * numberOfMeetings;
                            const coffeeCost = convertCentsToDollars(mb.coffee_cost_per_attendee, venueCurrency);
                            const coffeeTotal = coffeeCost * mb.coffee_count * attendees;

                            return (
                              <div className="pb-4 pl-14 space-y-2 border-t border-white/5 pt-3">
                                {numberOfMeetings > 0 && (
                                  <>
                                    {roomRental > 0 && (
                                      <div className="flex justify-between items-center text-sm">
                                        <span className="text-white/70">Room Rental ({numberOfMeetings}x)</span>
                                        <span className="text-white/80 font-medium">{formatCurrency(roomRental * numberOfMeetings)}</span>
                                      </div>
                                    )}
                                    {setupFees > 0 && (
                                      <div className="flex justify-between items-center text-sm">
                                        <span className="text-white/70">Setup Fees ({numberOfMeetings}x)</span>
                                        <span className="text-white/80 font-medium">{formatCurrency(setupFees * numberOfMeetings)}</span>
                                      </div>
                                    )}
                                    {avCost > 0 && (
                                      <div className="flex justify-between items-center text-sm">
                                        <span className="text-white/70">AV Equipment ({numberOfMeetings}x)</span>
                                        <span className="text-white/80 font-medium">{formatCurrency(avCost * numberOfMeetings)}</span>
                                      </div>
                                    )}
                                  </>
                                )}
                                {mb.coffee_count > 0 && (
                                  <div className="flex justify-between items-center text-sm">
                                    <span className="text-white/70">Coffee & Refreshments ({mb.coffee_count}x)</span>
                                    <span className="text-white/80 font-medium">{formatCurrency(coffeeTotal)}</span>
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                        </div>
                      );
                    })()
                  )}

                  {/* Activities - only show if set */}
                  {pricing.breakdown.activities !== null && (
                    (() => {
                      const activitiesPercent = pricing.totalEstimated
                        ? Math.round((pricing.breakdown.activities! / pricing.totalEstimated) * 100)
                        : null;

                      return (
                        <div className="py-2.5 border-b border-white/10">
                          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 flex-shrink-0 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${proposalTokens.colors.gold}33` }}>
                                <Sparkles className="w-4 h-4" style={{ color: proposalTokens.colors.gold }} />
                              </div>
                              <div>
                                <p className="text-white font-semibold">Activities</p>
                                <p className="text-white/50 text-sm">Team building & excursions</p>
                              </div>
                            </div>
                            <div className="flex items-center justify-between md:justify-end md:gap-6 md:text-right">
                              {activitiesPercent !== null ? (
                                <span className="text-white/50 text-sm">{activitiesPercent}%</span>
                              ) : null}
                              <span className="text-white text-lg md:min-w-[100px] md:text-right" style={{ fontFamily: proposalTokens.typography.fontFamily.sans.join(', ') }}>
                                {formatCurrency(pricing.breakdown.activities!)}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })()
                  )}

                  {/* Misc / Fees */}
                  {(() => {
                    const miscPercent = pricing.totalEstimated && pricing.breakdown.misc !== null
                      ? Math.round((pricing.breakdown.misc / pricing.totalEstimated) * 100)
                      : null;
                    const isExpanded = expandedSections.misc;
                    const miscItems = budgetBreakdown?.otherCosts?.miscellaneous_costs;
                    const hasBreakdown = miscItems && miscItems.length > 0;

                    return (
                      <div>
                        <div
                          className={`py-2.5 cursor-pointer transition-colors ${hasBreakdown ? 'hover:bg-white/5' : ''}`}
                          onClick={() => hasBreakdown && toggleSection('misc')}
                        >
                          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                            <div className="flex items-center gap-3 flex-1">
                              <div className="w-8 h-8 flex-shrink-0 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${proposalTokens.colors.gold}33` }}>
                                <Receipt className="w-4 h-4" style={{ color: proposalTokens.colors.gold }} />
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <p className="text-white font-semibold">Misc</p>
                                  {hasBreakdown && (
                                    <button
                                      className="text-white/50 hover:text-white transition-colors"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        toggleSection('misc');
                                      }}
                                    >
                                      {isExpanded ? (
                                        <ChevronUp className="w-4 h-4" />
                                      ) : (
                                        <ChevronDown className="w-4 h-4" />
                                      )}
                                    </button>
                                  )}
                                </div>
                                <p className="text-white/50 text-sm">All anticipated fees, taxes, and gratuities</p>
                              </div>
                            </div>
                            <div className="flex items-center justify-between md:justify-end md:gap-6 md:text-right">
                              {miscPercent !== null ? (
                                <span className="text-white/50 text-sm">{miscPercent}%</span>
                              ) : null}
                              <span className="text-white text-lg md:min-w-[100px] md:text-right" style={{ fontFamily: proposalTokens.typography.fontFamily.sans.join(', ') }}>
                                {pricing.breakdown.misc !== null ? formatCurrency(pricing.breakdown.misc) : '—'}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Misc Breakdown */}
                        {isExpanded && hasBreakdown && (() => {
                          const attendees = fullBudget?.attendees ?? groupSize;
                          const convertCentsToDollars = (cents: number | null, fromCurrency: string = venueCurrency): number => {
                            if (cents === null) return 0;
                            let dollars = cents / 100;
                            if (fromCurrency !== eventCurrency) {
                              dollars = convertCurrency(dollars, fromCurrency, eventCurrency);
                            }
                            return dollars;
                          };

                          return (
                            <div className="pb-4 pl-14 space-y-2 border-t border-white/5 pt-3">
                              {[...miscItems!].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)).map((item) => {
                                const itemDollars = convertCentsToDollars(item.amount, venueCurrency);
                                const itemTotal = item.type === 'per_person'
                                  ? itemDollars * attendees
                                  : itemDollars;
                                return (
                                  <div key={item.id} className="flex justify-between items-center text-sm">
                                    <div>
                                      <span className="text-white/70">{item.title || 'Untitled'}</span>
                                      <span className="text-white/40 ml-2 text-xs">
                                        {item.type === 'per_person'
                                          ? `${formatCurrency(itemDollars)}/person × ${attendees}`
                                          : 'flat'}
                                      </span>
                                    </div>
                                    <span className="text-white/80">{formatCurrency(Math.round(itemTotal))}</span>
                                  </div>
                                );
                              })}
                            </div>
                          );
                        })()}
                      </div>
                    );
                  })()}
                </div>

                {/* Budget Note (per-venue) */}
                {budgetBreakdown?.otherCosts?.budget_note && (
                  <div className="mt-4 pt-3 border-t border-white/10">
                    <p className="text-sm italic leading-relaxed text-white/70">
                      {budgetBreakdown.otherCosts.budget_note}
                    </p>
                  </div>
                )}

                {/* Totals Section */}
                {pricing.totals ? (
                  <>
                    {/* Subtotal */}
                    <div className="mt-4 pt-4 border-t-2 border-white/20">
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                        <p className="text-white/80 font-medium" style={{ fontFamily: proposalTokens.typography.fontFamily.sans.join(', ') }}>Subtotal</p>
                        <span className="text-white text-lg font-semibold md:min-w-[100px] md:text-right" style={{ fontFamily: proposalTokens.typography.fontFamily.sans.join(', ') }}>
                          {formatCurrency(pricing.totals.subtotal)}
                        </span>
                      </div>
                    </div>

                    {/* Contingency */}
                    {pricing.totals.contingency > 0 && (
                      <div className="mt-3 pt-3 border-t border-white/10">
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                          <div>
                            <p className="text-white/80 font-medium" style={{ fontFamily: proposalTokens.typography.fontFamily.sans.join(', ') }}>Contingency</p>
                            {pricing.settings && (
                              <p className="text-white/50 text-xs mt-0.5">{pricing.settings.contingency_fixed != null ? 'Fixed' : `${pricing.settings.contingency_percent}%`}</p>
                            )}
                          </div>
                          <span className="text-white text-lg font-semibold md:min-w-[100px] md:text-right" style={{ fontFamily: proposalTokens.typography.fontFamily.sans.join(', ') }}>
                            {formatCurrency(pricing.totals.contingency)}
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Planning Overhead */}
                    {pricing.totals.planningOverhead > 0 && (
                      <div className="mt-3 pt-3 border-t border-white/10">
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                          <div>
                            <p className="text-white/80 font-medium" style={{ fontFamily: proposalTokens.typography.fontFamily.sans.join(', ') }}>Planning Overhead</p>
                            {pricing.settings && (
                              <p className="text-white/50 text-xs mt-0.5">
                                {pricing.settings.planning_overhead_fixed !== null
                                  ? formatCurrency(pricing.settings.planning_overhead_fixed / 100)
                                  : `${pricing.settings.planning_overhead_percent}%`}
                              </p>
                            )}
                          </div>
                          <span className="text-white text-lg font-semibold md:min-w-[100px] md:text-right" style={{ fontFamily: proposalTokens.typography.fontFamily.sans.join(', ') }}>
                            {formatCurrency(pricing.totals.planningOverhead)}
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Profit Margin */}
                    {pricing.totals.profitMargin > 0 && (
                      <div className="mt-3 pt-3 border-t border-white/10">
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                          <div>
                            <p className="text-white/80 font-medium" style={{ fontFamily: proposalTokens.typography.fontFamily.sans.join(', ') }}>Profit Margin</p>
                            {pricing.settings && (
                              <p className="text-white/50 text-xs mt-0.5">
                                {pricing.settings.profit_margin_fixed !== null
                                  ? formatCurrency(pricing.settings.profit_margin_fixed / 100)
                                  : `${pricing.settings.profit_margin_percent}%`}
                              </p>
                            )}
                          </div>
                          <span className="text-white text-lg font-semibold md:min-w-[100px] md:text-right" style={{ fontFamily: proposalTokens.typography.fontFamily.sans.join(', ') }}>
                            {formatCurrency(pricing.totals.profitMargin)}
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Total Program Cost */}
                    <div className="mt-4 pt-4 border-t-2" style={{ borderColor: `${proposalTokens.colors.gold}4d` }}>
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 flex-shrink-0 rounded-lg flex items-center justify-center" style={{ backgroundColor: proposalTokens.colors.gold }}>
                            <CircleDollarSign className="w-5 h-5 text-black" />
                          </div>
                          <p className="text-white font-bold text-lg" style={{ fontFamily: proposalTokens.typography.fontFamily.sans.join(', ') }}>
                            Total Program Cost ({eventCurrency})
                          </p>
                        </div>
                        <span className="text-2xl font-bold md:min-w-[100px] md:text-right" style={{ color: proposalTokens.colors.gold, fontFamily: proposalTokens.typography.fontFamily.sans.join(', ') }}>
                          {formatCurrency(pricing.totals.totalProgramCost)}
                        </span>
                      </div>
                    </div>

                    {/* Per Person */}
                    {pricing.perPerson !== null && (
                      <div className="mt-4 pt-4 border-t border-white/10">
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                          <p className="text-white/70 font-medium" style={{ fontFamily: proposalTokens.typography.fontFamily.sans.join(', ') }}>Per Person</p>
                          <span className="text-white text-xl font-semibold md:min-w-[100px] md:text-right" style={{ fontFamily: proposalTokens.typography.fontFamily.sans.join(', ') }}>
                            {formatCurrency(pricing.perPerson)}
                          </span>
                        </div>
                      </div>
                    )}
                  </>
                ) : pricing.totalEstimated !== null ? (
                  <>
                    {/* Fallback: Simple Total */}
                    <div className="mt-6 pt-6 border-t-2" style={{ borderColor: `${proposalTokens.colors.gold}4d` }}>
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 flex-shrink-0 rounded-lg flex items-center justify-center" style={{ backgroundColor: proposalTokens.colors.gold }}>
                            <CircleDollarSign className="w-5 h-5 text-black" />
                          </div>
                          <p className="text-white font-bold text-lg" style={{ fontFamily: proposalTokens.typography.fontFamily.sans.join(', ') }}>Total Cost</p>
                        </div>
                        <div className="flex items-center justify-between md:justify-end md:gap-6 md:text-right">
                          <span className="font-semibold" style={{ color: proposalTokens.colors.gold }}>100%</span>
                          <span className="text-2xl font-bold md:min-w-[100px] md:text-right" style={{ color: proposalTokens.colors.gold, fontFamily: proposalTokens.typography.fontFamily.sans.join(', ') }}>
                            {formatCurrency(pricing.totalEstimated)}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Per Person */}
                    {pricing.perPerson !== null && (
                      <div className="mt-4 pt-4 border-t border-white/10">
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                          <p className="text-white/70 font-medium" style={{ fontFamily: proposalTokens.typography.fontFamily.sans.join(', ') }}>Per Person</p>
                          <span className="text-white text-xl font-semibold md:min-w-[100px] md:text-right" style={{ fontFamily: proposalTokens.typography.fontFamily.sans.join(', ') }}>
                            {formatCurrency(pricing.perPerson)}
                          </span>
                        </div>
                      </div>
                    )}
                  </>
                ) : null}

              </div>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* Return to Proposal */}
      {onReturnToProposal && (
        <div className="flex justify-center py-12">
          <button
            onClick={onReturnToProposal}
            className="group flex items-center gap-2 px-6 py-3 rounded-full border-2 transition-all duration-200 hover:shadow-lg"
            style={{
              borderColor: proposalTokens.colors.teal,
              color: proposalTokens.colors.teal,
              fontFamily: proposalTokens.typography.fontFamily.sans.join(', '),
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = proposalTokens.colors.teal;
              e.currentTarget.style.color = '#ffffff';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = proposalTokens.colors.teal;
            }}
          >
            <ChevronLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1" />
            <span className="font-semibold text-sm">Return to Proposal</span>
          </button>
        </div>
      )}

    </div>
  );
}
